import React from "react";
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { RightPanel } from "../../../../../../../components/RightPanel";
import { DynamicFormRenderer } from "../../../../../../CustomFormFields/DynamicFormRenderer";
import { fetchTemplateByCode } from "../../../../../../../redux/CustomFormFields/CustomFormFieldsActions";
import { ITemplateField } from "../../../../../../../redux/CustomFormFields/CustomFormFields.types";
import { IBusinessPolicyChangeStepsProps } from "../PolicyChange.types";
import { AddOutlined } from "@mui/icons-material";
import produce from "immer";

const TEMPLATE_CODE = "XTL4OJ";

export const Underwriting: React.FC<IBusinessPolicyChangeStepsProps> = ({
  values,
  setValues,
  isStatusCompleted,
  autoSaveData = () => {},
}) => {
  const [open, setOpen] = React.useState<{
    type: "create" | "edit";
    index?: number;
    data?: ITemplateField[];
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
  const [templateFields, setTemplateFields] = React.useState<ITemplateField[] | null>(null);

  React.useEffect(() => {
    fetchTemplateByCode(TEMPLATE_CODE).then((res) => {
      if (res && res.fields) {
        setTemplateFields(res.fields);
      }
    });
  }, []);

  const handleCreate = () => {
    setOpen({ type: "create" });
  };

  const handleEdit = (index: number) => {
    setOpen({ type: "edit", index, data: values.underwriting[index] });
  };

  const handleSaveSuccess = (fields: ITemplateField[]) => {
    const newValues = produce(values, (draftValues) => {
      if (open && open.type === "edit" && open.index !== undefined) {
        draftValues.underwriting[open.index] = fields;
      } else {
        draftValues.underwriting.push(fields);
      }
    });
    setValues(newValues);
    autoSaveData(newValues);
    setOpen(null);
  };

  const handleDelete = (index: number) => {
    setDeleteConfirm(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm === null || deleteConfirm >= values.underwriting.length) {
      return;
    }
    const newValues = produce(values, (draftValues) => {
      draftValues.underwriting.splice(deleteConfirm, 1);
    });
    setValues(newValues);
    autoSaveData(newValues);
    setDeleteConfirm(null);
  };

  return (
    <>
      <LabelCard heading="Underwriting Notes" />
      <Stack direction={"row"} justifyContent={"end"} mt={2}>
        <Button
          variant="contained"
          disabled={isStatusCompleted}
          onClick={handleCreate}
          startIcon={<AddOutlined />}
        >
          Create
        </Button>
      </Stack>
      {values.underwriting.map((record, rowIndex) => (
        <TableContainer key={rowIndex} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <span>#{rowIndex + 1}</span>
                    <StandardTableActions
                      onEditClick={
                        !isStatusCompleted
                          ? () => handleEdit(rowIndex)
                          : undefined
                      }
                      onDeleteClick={
                        !isStatusCompleted
                          ? () => handleDelete(rowIndex)
                          : undefined
                      }
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templateFields &&
                templateFields.map((field, colIndex) => (
                  <TableRow key={colIndex}>
                    <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                      {field.title}
                    </TableCell>
                    <TableCell>{record[colIndex]?.value ?? ""}</TableCell>
                    <TableCell>{record[colIndex]?.description ?? ""}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      {open && (
        <RightPanel
          open={true}
          heading="Underwriting Notes"
          width="50%"
          onClose={() => setOpen(null)}
        >
          <DynamicFormRenderer
            templateCode={TEMPLATE_CODE}
            fields={open.type === "edit" ? open.data ?? null : null}
            onSubmit={handleSaveSuccess}
          />
        </RightPanel>
      )}

      {deleteConfirm !== null && (
        <ConfirmDialog
          open
          content="Are you sure you want to delete this record?"
          onClose={() => setDeleteConfirm(null)}
          onConfrim={handleDeleteConfirm}
        />
      )}
    </>
  );
};
